import { container, singleton } from "tsyringe";
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Poll, PollCreationInformation, PollInstanceStatus } from "../models/poll";
import { PTSocketService } from "./ptsocket.service";
import { PTApiService } from "./ptapi-service";
import { Config } from "../config";
import { filter, map } from "rxjs/operators";

@singleton()
export class PollService {
    // using DI in constructor would require to eject the app
    private ptSocketService = container.resolve(PTSocketService);
    private ptApiService = container.resolve(PTApiService);

    private polls$ = new BehaviorSubject<Poll[]>([]);
    private seenPollIds$ = new BehaviorSubject<string[]>([]);

    public get pollsObservable() { return this.polls$.asObservable(); }

    public get unseenPollsCountObservable(): Observable<number> {
        return combineLatest([this.polls$, this.seenPollIds$]).pipe(
            map(([polls, seenIds]) => polls.map(p => p.pollGuid).filter(id => !seenIds.includes(id)).length)
        );
    }

    public async fetchPolls() {
        const statusesToFetch = [PollInstanceStatus.ACTIVE, PollInstanceStatus.AVAILABLE, PollInstanceStatus.COMPLETED];
        const pollResults = await Promise.all(statusesToFetch.map(s => this.ptApiService.fetchPolls(s)));
        const newPolls = pollResults.filter(p => !!p).flat();
        this.polls$.next(newPolls);
    }

    public markPollIdsAsSeen(pollIds: string[]) {
        const newSeenIds = pollIds.filter(p => !this.seenPollIds$.value.includes(p));
        // Only trigger observable if there are newly seen poll ids
        if (newSeenIds.length > 0) {
            this.seenPollIds$.next([...this.seenPollIds$.value, ...newSeenIds]);
        }
    }

    public bindToSocket() {
        return this.ptSocketService.pollUpdateObservable
            .pipe(
                filter(u => !!u)
            )
            .subscribe(pollUpdate => {
                let polls = [...this.polls$.value];
                const poll = polls.find(p => p.pollGuid === pollUpdate.pollData.pollGuid);

                if (pollUpdate.notificationType === 'DELETE') {
                    polls = polls.filter(p => p.pollGuid !== pollUpdate.pollData.pollGuid);
                } else {
                    if (poll) {
                        Object.assign(poll, pollUpdate.pollData);
                    } else {
                        polls.push(pollUpdate.pollData);
                    }
                }

                this.polls$.next(polls);
            });
    }
}
